import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, MapPin, Plus, Edit3, Trash2, Star, StarOff,
  Home as HomeIcon, Briefcase, Store, Package, Loader2, Crosshair, Phone,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Label catalogue ─── one source of truth keeps the form, list and
// markers visually consistent and lets us swap icons later without
// touching multiple files.
const LABELS = [
  { id: "home",      ar: "بيت",   icon: HomeIcon,  bg: "#15803D" },
  { id: "work",      ar: "عمل",   icon: Briefcase, bg: "#2563EB" },
  { id: "market",    ar: "ماركت", icon: Store,     bg: "#E85D45" },
  { id: "warehouse", ar: "مخزن",  icon: Package,   bg: "#7C3AED" },
  { id: "other",     ar: "آخر",   icon: MapPin,    bg: "#6B7280" },
];
const labelMeta = (id) => LABELS.find((l) => l.id === id) || LABELS[LABELS.length - 1];

// Pin icon factory — color tied to label_type so a glance at the map tells
// the user "blue=work, red=market" etc.
const makePinIcon = (color = "#15803D", isDefault = false) =>
  L.divIcon({
    className: "",
    iconSize: [44, 56],
    iconAnchor: [22, 56],
    html: `
      <div style="position:relative;text-align:center;">
        <div style="background:${color};color:white;border-radius:50% 50% 50% 0;width:36px;height:36px;display:flex;align-items:center;justify-content:center;margin:0 auto;border:3px solid white;box-shadow:0 4px 8px rgba(0,0,0,0.35);transform:rotate(-45deg);">
          <span style="transform:rotate(45deg);font-size:18px;">📍</span>
        </div>
        ${isDefault ? `<div style="position:absolute;top:-6px;right:-2px;background:#F5A623;color:white;border-radius:50%;width:18px;height:18px;display:flex;align-items:center;justify-content:center;font-size:11px;border:2px solid white;">★</div>` : ""}
      </div>`,
  });

// Click-to-pick handler that records lat/lng wherever the user taps the map.
const ClickPicker = ({ onPick }) => {
  useMapEvents({ click: (e) => onPick({ lat: e.latlng.lat, lng: e.latlng.lng }) });
  return null;
};

// Imperative helper that pans the map when `center` prop changes — used
// when the user clicks "GPS الحالي" so the map jumps to their position.
const FlyTo = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo([center.lat, center.lng], Math.max(map.getZoom(), 16), { duration: 0.6 });
  }, [center, map]);
  return null;
};

const emptyForm = {
  label_type: "home",
  name: "",
  area_note: "",
  alt_phone: "",
  lat: 33.3152,
  lng: 44.3661,
  is_default: false,
};

const MyLocations = () => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // null | "new" | locId
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [flyTarget, setFlyTarget] = useState(null);

  const fetchAll = async () => {
    try {
      const { data } = await axios.get(`${API}/me/saved-locations`);
      setLocations(data.locations || []);
    } catch {
      toast.error("فشل تحميل المواقع");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const startNew = () => {
    setForm({ ...emptyForm, is_default: locations.length === 0 });
    setEditor("new");
    // Try to auto-center on user's GPS for convenience.
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const here = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setForm((f) => ({ ...f, ...here }));
          setFlyTarget(here);
        },
        () => {},
        { enableHighAccuracy: true, timeout: 6000 }
      );
    }
  };

  const startEdit = (loc) => {
    setForm({
      label_type: loc.label_type || "other",
      name: loc.name || "",
      area_note: loc.area_note || "",
      alt_phone: loc.alt_phone || "",
      lat: loc.lat,
      lng: loc.lng,
      is_default: !!loc.is_default,
    });
    setEditor(loc.id);
    setFlyTarget({ lat: loc.lat, lng: loc.lng });
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("اكتب اسم/تسمية للموقع (مثلاً: بيت أبو حسين)");
      return;
    }
    setSaving(true);
    try {
      if (editor === "new") {
        await axios.post(`${API}/me/saved-locations`, form);
        toast.success("تم حفظ الموقع");
      } else {
        await axios.patch(`${API}/me/saved-locations/${editor}`, form);
        toast.success("تم تحديث الموقع");
      }
      setEditor(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحفظ");
    } finally { setSaving(false); }
  };

  const remove = async (loc) => {
    if (!window.confirm(`حذف الموقع «${loc.name}»؟`)) return;
    try {
      await axios.delete(`${API}/me/saved-locations/${loc.id}`);
      toast.success("تم الحذف");
      fetchAll();
    } catch { toast.error("فشل الحذف"); }
  };

  const setDefault = async (loc) => {
    try {
      await axios.post(`${API}/me/saved-locations/${loc.id}/set-default`);
      toast.success(`«${loc.name}» صار الموقع الرئيسي`);
      fetchAll();
    } catch { toast.error("فشل التحديث"); }
  };

  const useCurrentGps = () => {
    if (!navigator.geolocation) {
      toast.error("الجهاز ما يدعم تحديد الموقع");
      return;
    }
    toast.info("جاري تحديد موقعك…");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const here = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setForm((f) => ({ ...f, ...here }));
        setFlyTarget(here);
        toast.success("تم تحديد موقعك الحالي");
      },
      () => toast.error("فشل تحديد الموقع — تأكد من تشغيل GPS"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const center = useMemo(() => [form.lat, form.lng], [form.lat, form.lng]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F0F9FF] to-white pb-24">
      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-[#E4EBE6] px-4 py-3 flex items-center gap-3 shadow-sm">
        <button
          onClick={() => navigate(-1)}
          data-testid="locations-back-btn"
          className="p-2 rounded-full hover:bg-[#F0F9FF]"
        >
          <ArrowRight className="w-5 h-5 text-[#2D4A3E]" />
        </button>
        <h1 className="font-heading font-bold text-xl text-[#111814] flex items-center gap-2">
          <MapPin className="w-5 h-5 text-[#E85D45]" />
          مواقعي
        </h1>
        <div className="flex-1" />
        {editor === null && (
          <button
            onClick={startNew}
            data-testid="add-location-btn"
            className="bg-[#15803D] hover:bg-[#126b32] text-white text-sm font-bold px-4 py-2 rounded-full flex items-center gap-1.5 transition-colors shadow"
          >
            <Plus className="w-4 h-4" />
            موقع جديد
          </button>
        )}
      </div>

      <div className="max-w-3xl mx-auto px-4 pt-4">
        {/* Editor */}
        <AnimatePresence>
          {editor !== null && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-white rounded-3xl shadow-lg border-2 border-[#15803D] p-4 mb-6"
            >
              <h2 className="font-bold text-lg text-[#111814] mb-4 flex items-center gap-2">
                {editor === "new" ? <><Plus className="w-5 h-5 text-[#15803D]" />موقع جديد</> : <><Edit3 className="w-5 h-5 text-[#2563EB]" />تعديل الموقع</>}
              </h2>

              {/* Label type — chips */}
              <div className="mb-3">
                <p className="text-xs font-bold text-[#5B6B62] mb-2">نوع الموقع:</p>
                <div className="flex flex-wrap gap-2">
                  {LABELS.map((l) => {
                    const Icon = l.icon;
                    const active = form.label_type === l.id;
                    return (
                      <button
                        key={l.id}
                        onClick={() => setForm((f) => ({ ...f, label_type: l.id }))}
                        data-testid={`label-${l.id}`}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-bold transition-all ${active ? "text-white shadow-md scale-105" : "bg-[#F0F9FF] text-[#2D4A3E] hover:bg-[#E0F2FE]"}`}
                        style={active ? { background: l.bg } : {}}
                      >
                        <Icon className="w-4 h-4" />
                        {l.ar}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Name */}
              <div className="mb-3">
                <label className="text-xs font-bold text-[#5B6B62] mb-1 block">
                  اسم الموقع (مثلاً: بيت أبو حسين / ماركت الورود)
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  data-testid="location-name-input"
                  placeholder="اسم واضح يفرّق هاي عن باقي مواقعك"
                  className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#15803D] bg-white"
                />
              </div>

              {/* Area note */}
              <div className="mb-3">
                <label className="text-xs font-bold text-[#5B6B62] mb-1 block">
                  أقرب نقطة دالة / المنطقة (اختياري)
                </label>
                <input
                  type="text"
                  value={form.area_note}
                  onChange={(e) => setForm((f) => ({ ...f, area_note: e.target.value }))}
                  data-testid="location-area-input"
                  placeholder="مثلاً: شارع فلسطين، يم جامع النور"
                  className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#15803D] bg-white"
                />
              </div>

              {/* Alt phone */}
              <div className="mb-3">
                <label className="text-xs font-bold text-[#5B6B62] mb-1 block">
                  رقم بديل للتوصيل (اختياري)
                </label>
                <input
                  type="tel"
                  value={form.alt_phone}
                  onChange={(e) => setForm((f) => ({ ...f, alt_phone: e.target.value }))}
                  data-testid="location-altphone-input"
                  placeholder="07XXXXXXXXX"
                  className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#15803D] bg-white"
                />
              </div>

              {/* Map picker */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold text-[#5B6B62]">حدّد الموقع على الخريطة (اضغط مكانه):</p>
                  <button
                    onClick={useCurrentGps}
                    data-testid="use-gps-btn"
                    className="text-xs font-bold text-[#2563EB] hover:underline flex items-center gap-1"
                  >
                    <Crosshair className="w-3.5 h-3.5" />
                    موقعي الحالي
                  </button>
                </div>
                <div className="rounded-2xl overflow-hidden border-2 border-[#E4EBE6]" style={{ height: 280 }}>
                  <MapContainer
                    center={center}
                    zoom={15}
                    style={{ height: "100%", width: "100%" }}
                    scrollWheelZoom={true}
                  >
                    <TileLayer
                      attribution='&copy; OpenStreetMap'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker
                      position={[form.lat, form.lng]}
                      icon={makePinIcon(labelMeta(form.label_type).bg, form.is_default)}
                      draggable={true}
                      eventHandlers={{
                        dragend: (e) => {
                          const ll = e.target.getLatLng();
                          setForm((f) => ({ ...f, lat: ll.lat, lng: ll.lng }));
                        },
                      }}
                    />
                    <ClickPicker onPick={(p) => setForm((f) => ({ ...f, ...p }))} />
                    <FlyTo center={flyTarget} />
                  </MapContainer>
                </div>
                <p className="text-[11px] text-[#5B6B62] mt-1.5">
                  الإحداثيات: {form.lat.toFixed(5)}, {form.lng.toFixed(5)}
                </p>
              </div>

              {/* Default toggle */}
              <label className="flex items-center gap-2 mb-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_default}
                  onChange={(e) => setForm((f) => ({ ...f, is_default: e.target.checked }))}
                  data-testid="set-default-checkbox"
                  className="w-4 h-4 accent-[#15803D]"
                />
                <span className="text-sm text-[#111814]">
                  ⭐ خلّيه الموقع الرئيسي (المعتمد افتراضياً للتوصيل)
                </span>
              </label>

              <div className="flex gap-2">
                <button
                  onClick={save}
                  disabled={saving}
                  data-testid="save-location-btn"
                  className="flex-1 bg-[#15803D] hover:bg-[#126b32] disabled:opacity-60 text-white font-bold py-3 rounded-full flex items-center justify-center gap-2 transition-colors"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                  {editor === "new" ? "حفظ الموقع" : "تحديث"}
                </button>
                <button
                  onClick={() => setEditor(null)}
                  data-testid="cancel-location-btn"
                  className="px-6 bg-[#F0F9FF] hover:bg-[#E0F2FE] text-[#2D4A3E] font-bold py-3 rounded-full transition-colors"
                >
                  إلغاء
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Locations list */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[#15803D]" />
          </div>
        ) : locations.length === 0 && editor === null ? (
          <div className="text-center py-12 text-[#5B6B62]">
            <MapPin className="w-12 h-12 mx-auto mb-3 text-[#BAE6FD]" />
            <p className="font-bold text-lg text-[#111814]">ما عدك مواقع محفوظة</p>
            <p className="text-sm mb-4">احفظ موقع البيت، العمل، الماركت — تختاره عند الطلب بسرعة.</p>
            <button
              onClick={startNew}
              data-testid="empty-add-btn"
              className="bg-[#15803D] hover:bg-[#126b32] text-white font-bold px-6 py-3 rounded-full inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              أضف أول موقع
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {locations.map((loc) => {
              const meta = labelMeta(loc.label_type);
              const Icon = meta.icon;
              return (
                <div
                  key={loc.id}
                  data-testid={`location-card-${loc.id}`}
                  className="bg-white rounded-2xl shadow-md border border-[#E4EBE6] p-4 flex gap-3"
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-white shrink-0"
                    style={{ background: meta.bg }}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-bold text-[#111814] truncate">{loc.name}</h3>
                      {loc.is_default && (
                        <span className="bg-[#FEF3C7] text-[#92400E] text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Star className="w-3 h-3 fill-current" />
                          الرئيسي
                        </span>
                      )}
                      <span className="bg-[#F0F9FF] text-[#2D4A3E] text-[10px] font-bold px-2 py-0.5 rounded-full">
                        {meta.ar}
                      </span>
                    </div>
                    {loc.area_note && (
                      <p className="text-xs text-[#5B6B62] truncate flex items-center gap-1 mb-0.5">
                        <MapPin className="w-3 h-3 shrink-0" />
                        {loc.area_note}
                      </p>
                    )}
                    {loc.alt_phone && (
                      <p className="text-xs text-[#5B6B62] flex items-center gap-1">
                        <Phone className="w-3 h-3 shrink-0" />
                        {loc.alt_phone}
                      </p>
                    )}
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => startEdit(loc)}
                        data-testid={`edit-loc-${loc.id}`}
                        className="text-xs bg-[#F0F9FF] text-[#2563EB] hover:bg-[#E0F2FE] font-bold px-3 py-1.5 rounded-full flex items-center gap-1"
                      >
                        <Edit3 className="w-3 h-3" />
                        تعديل
                      </button>
                      {!loc.is_default && (
                        <button
                          onClick={() => setDefault(loc)}
                          data-testid={`default-loc-${loc.id}`}
                          className="text-xs bg-[#FEF3C7] text-[#92400E] hover:bg-[#FDE68A] font-bold px-3 py-1.5 rounded-full flex items-center gap-1"
                        >
                          <Star className="w-3 h-3" />
                          خلّيه الرئيسي
                        </button>
                      )}
                      <button
                        onClick={() => remove(loc)}
                        data-testid={`del-loc-${loc.id}`}
                        className="text-xs bg-[#FEE2E2] text-[#B91C1C] hover:bg-[#FECACA] font-bold px-3 py-1.5 rounded-full flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" />
                        حذف
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyLocations;
